import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { s as stringOr, d as doPreventDefault } from "./helpers-674591c1.mjs";
import { u as useDefaults } from "./_plugin-vue_export-helper-3194dc60.mjs";
import "./i18n-35335a5c.mjs";
import { b as boundUseI18n } from "./_i18n-e414d464.mjs";
const doContactItemComponentSetup = (props, defaultsName) => {
  const { t } = boundUseI18n();
  const ready = ref(false);
  onMounted(() => {
    ready.value = true;
  });
  onBeforeUnmount(() => {
    ready.value = false;
  });
  const predefined = useDefaults(props, "VListItem");
  const withDefaults = useDefaults(predefined, defaultsName);
  const user = ref(props.user);
  const name = computed(
    () => {
      var _a, _b, _c;
      return stringOr(
        ((_a = user.value) == null ? void 0 : _a.name) || void 0,
        stringOr(
          ((_b = user.value) == null ? void 0 : _b.friendlyName) || void 0,
          stringOr(((_c = user.value) == null ? void 0 : _c.identifier) || void 0)
        )
      );
    }
  );
  const online = computed(
    () => (
      // @ts-ignore
      ready.value && user.value ? user.value.state.online : void 0
    )
  );
  const loading = computed(
    () => ready.value !== true || void 0 === user.value || "string" !== typeof name.value || online.value === void 0
  );
  const dotColor = computed(() => {
    switch (online.value) {
      case true:
        return "success";
      case false:
        return "cancel";
      default:
        return "question";
    }
  });
  const useContactAvatar = computed(() => online.value === null);
  const contactAvatarBindings = computed(() => {
    var _a;
    return {
      identity: (_a = user.value) == null ? void 0 : _a.identifier,
      size: 20
    };
  });
  const bindings = computed(() => ({
    ...withDefaults,
    user: void 0,
    title: loading.value ? t("$vuetify.loading") : name.value,
    selectable: void 0
  }));
  watch(
    () => props.user,
    (u) => {
      user.value = u;
    },
    {
      immediate: true
    }
  );
  return {
    src: user,
    name,
    online,
    loading,
    dotColor,
    bindings,
    doPreventDefault,
    useContactAvatar,
    contactAvatarBindings
  };
};
const filesFileTypes = [
  "image/bmp",
  "image/tiff",
  "text/vcard",
  "text/x-vcard",
  "text/csv",
  "text/rtf",
  "text/richtext",
  "text/calendar",
  "text/directory",
  "application/pdf",
  "application/vcard",
  "application/vnd.apple.pkpass",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
const visualsFileTypes = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "video/mpeg",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/3gpp",
  "video/3gpp2",
  "video/3gpp-tt",
  "video/H261",
  "video/H263",
  "video/H263-1998",
  "video/H263-2000",
  "video/H264"
];
const imageFileTypes = ["image/png", "image/jpeg", "image/gif"];
const videoFileTypes = [
  "video/mpeg",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/3gpp",
  "video/3gpp2",
  "video/3gpp-tt",
  "video/H261",
  "video/H263",
  "video/H263-1998",
  "video/H263-2000",
  "video/H264"
];
const audiblesFileTypes = [
  "audio/basic",
  "audio/L24",
  "audio/mp4",
  "audio/mpeg",
  "audio/ogg",
  "audio/vnd.rn-realaudio",
  "audio/vnd.wave",
  "audio/3gpp",
  "audio/3gpp2",
  "audio/ac3",
  "audio/webm",
  "audio/amr-nb",
  "audio/amr",
  "audio/aac",
  "audio/ogg; codecs=opus"
];
const acceptableFileTypes = [
  .../* @__PURE__ */ new Set([...filesFileTypes, ...visualsFileTypes, ...audiblesFileTypes])
];
const extensionMimes = {
  ".bmp": ["image/bmp"],
  ".tif": ["image/tiff"],
  ".tiff": ["image/tiff"],
  ".vcf": ["text/vcard", "text/x-vcard", "application/vcard"],
  ".csv": ["text/csv"],
  ".rtf": ["text/rtf"],
  ".rtx": ["text/richtext"],
  ".ics": ["text/calendar"],
  ".pdf": ["application/pdf"],
  ".pkpass": ["application/vnd.apple.pkpass"],
  ".doc": [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ],
  ".xls": ["application/vnd.ms-excel"],
  ".ppt": [
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ],
  ".pptx": [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ],
  ".xlsx": [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ],
  ".png": ["image/png"],
  ".jpg": ["image/jpeg"],
  ".jpeg": ["image/jpeg"],
  ".gif": ["image/gif"],
  ".mpg": ["video/mpeg"],
  ".mpeg": ["video/mpeg"],
  ".mp4": ["video/mp4", "audio/mp4"],
  ".mov": ["video/quicktime"],
  ".webm": ["video/webm", "audio/webm"],
  ".3gp": ["video/3gpp", "audio/3gpp"],
  ".3g2": ["video/3gpp2", "audio/3gpp2"],
  ".h261": ["video/H261"],
  ".h263": ["video/H263"],
  ".h264": ["video/H264"],
  ".au": ["audio/basic"],
  ".snd": ["audio/basic"],
  ".l24": ["audio/L24"],
  ".mp3": ["audio/mpeg"],
  ".ra": ["audio/vnd.rn-realaudio"],
  ".wav": ["audio/vnd.wave"],
  ".ac3": ["audio/ac3"],
  ".amr": ["audio/amr-nb", "audio/amr"],
  ".aac": ["audio/aac"],
  ".opus": ["audio/ogg"],
  ".ogg": ["audio/ogg"]
};
const getMimeByExtension = (ext) => extensionMimes[ext] || [];
const getMimeForFile = (file) => {
  if (file.type) {
    return file.type;
  }
  const ext = file.name.split(".").pop();
  const mimes = getMimeByExtension(`.${ext}`);
  return mimes.length ? mimes[0] : "";
};
export {
  acceptableFileTypes as a,
  audiblesFileTypes as b,
  visualsFileTypes as c,
  doContactItemComponentSetup as d,
  filesFileTypes as f,
  getMimeForFile as g,
  imageFileTypes as i,
  videoFileTypes as v
};
//# sourceMappingURL=_common-fcf3ef61.mjs.map
